const AXIS_TICK_STATE_KEYS = [
  'dtick',
  'tick0',
  'tickmode',
  'tickvals',
  'ticktext',
  'minor',
  'nticks',
  'tickformat',
  'exponentformat',
  'showexponent',
  'range',
  'fixedrange',
];

/**
 * Plotly log axes: default minor tick labels (2, 5, …) read as non-monotonic.
 * dtick:1 works in many cases; explicit decade arrays are more reliable for finicky charts.
 */
export function plotlyLogAxisTickConfig() {
  return {
    dtick: 1,
    minor: { showticklabels: false },
  };
}

function parsePositiveNumber(v) {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Collect finite positive numeric values from trace axis arrays (for log tick ranges). */
export function collectPositiveAxisValuesFromTraces(traces, axisKey = 'y') {
  const values = [];
  for (const trace of traces || []) {
    const arr = trace?.[axisKey];
    if (!Array.isArray(arr)) continue;
    for (const v of arr) {
      const n = parsePositiveNumber(v);
      if (n != null) values.push(n);
    }
  }
  return values;
}

/** Collect finite positive values from raw table rows (fallback when trace arrays are sparse). */
export function collectPositiveAxisValuesFromTable(rows, columnName) {
  if (!Array.isArray(rows) || !columnName) return [];
  const values = [];
  for (const row of rows) {
    const n = parsePositiveNumber(row?.[columnName]);
    if (n != null) values.push(n);
  }
  return values;
}

/** Resolve positive axis values from traces first, then table column(s). */
export function collectPositiveAxisValuesForChart(chart, tableData, traces, axisKey = 'y') {
  const fromTraces = collectPositiveAxisValuesFromTraces(traces, axisKey);
  if (fromTraces.length) return fromTraces;
  if (!chart || !Array.isArray(tableData)) return [];

  if (axisKey === 'x') {
    return collectPositiveAxisValuesFromTable(tableData, chart.XAxisColumn);
  }

  let yCols = [];
  if (chart.YAxisColumn) {
    yCols = String(chart.YAxisColumn)
      .split(',')
      .map((col) => col.trim())
      .filter(Boolean);
  } else if (Array.isArray(chart.YAxisColumns)) {
    yCols = chart.YAxisColumns.map((col) => String(col).trim()).filter(Boolean);
  }
  const groupCol = chart.GroupColumn && String(chart.GroupColumn).trim();
  if (groupCol) {
    yCols = yCols.filter((col) => col !== groupCol);
  }

  const values = [];
  for (const col of yCols) {
    values.push(...collectPositiveAxisValuesFromTable(tableData, col));
  }
  return values;
}

/**
 * Build a Plotly axis config when switching linear ↔ log (e.g. expanded plot modal).
 * Strips stale tick/range state from the other scale so log axes do not show 1–9 repeats.
 */
export function buildPlotlyAxisForScale(baseAxis = {}, scale = 'linear', options = {}) {
  const { preserveCategory = false } = options;

  if (preserveCategory && baseAxis.type === 'category') {
    return { ...baseAxis, type: 'category' };
  }

  const axis = { ...baseAxis };
  for (const key of AXIS_TICK_STATE_KEYS) {
    delete axis[key];
  }

  axis.type = scale === 'log' ? 'log' : 'linear';
  axis.autorange = true;

  if (scale === 'log') {
    Object.assign(axis, plotlyLogAxisTickConfig());
  }

  return axis;
}

/** Format a positive value as `1E-05`, `1E+01`, etc. */
export function formatScientificTickLabel(value) {
  if (!Number.isFinite(value) || value <= 0) {
    return '';
  }

  const exponent = Math.round(Math.log10(value));
  const isDecade = Math.abs(value - Math.pow(10, exponent)) <= 1e-6 * Math.max(value, 1);

  if (isDecade) {
    const expPart =
      exponent >= 0
        ? `+${String(exponent).padStart(2, '0')}`
        : `-${String(Math.abs(exponent)).padStart(2, '0')}`;
    return `1E${expPart}`;
  }

  const [mantissa, exp] = value.toExponential(0).toLowerCase().split('e');
  const expNum = parseInt(exp, 10);
  const expPart =
    expNum >= 0
      ? `+${String(expNum).padStart(2, '0')}`
      : `-${String(Math.abs(expNum)).padStart(2, '0')}`;
  const normalizedMantissa = String(parseFloat(mantissa));
  return `${normalizedMantissa}E${expPart}`;
}

/** Plain decade labels for simple log axes: 1, 10, 100, 0.01, … */
export function formatSimpleLogTickLabel(value) {
  if (!Number.isFinite(value) || value <= 0) {
    return "";
  }

  const exponent = Math.round(Math.log10(value));
  const decade = Math.pow(10, exponent);
  if (Math.abs(value - decade) > 1e-6 * Math.max(decade, 1)) {
    return String(value);
  }

  if (exponent >= 0) {
    return String(decade);
  }

  return decade.toFixed(-exponent);
}

/**
 * Explicit powers-of-ten ticks with plain numeric labels (simple log).
 * Avoids Plotly showing a single tick (e.g. only "100") on moderate-value ranges.
 */
export function logAxisSimpleDecadeTickConfig(values) {
  const positiveValues = (Array.isArray(values) ? values : [])
    .map((v) => (typeof v === "number" ? v : parseFloat(v)))
    .filter((v) => Number.isFinite(v) && v > 0);

  if (!positiveValues.length) {
    return { ...plotlyLogAxisTickConfig(), autorange: true };
  }

  const minValue = Math.min(...positiveValues);
  const maxValue = Math.max(...positiveValues);
  let minExponent = Math.floor(Math.log10(minValue));
  let maxExponent = Math.ceil(Math.log10(maxValue));

  if (minExponent === maxExponent) {
    minExponent -= 1;
    maxExponent += 1;
  }

  const tickvals = [];
  for (let exponent = minExponent; exponent <= maxExponent; exponent += 1) {
    tickvals.push(Math.pow(10, exponent));
  }

  return {
    tickmode: "array",
    tickvals,
    ticktext: tickvals.map(formatSimpleLogTickLabel),
    // Plotly log axes expect range in log10 coordinates (e.g. [2, 3] → 100–1000).
    range: [minExponent, maxExponent],
    autorange: false,
    minor: { showticklabels: false, showgrid: false },
    showexponent: "none",
  };
}

/** @deprecated Use {@link formatScientificTickLabel} — kept for existing imports. */
export function formatLogPowerTickLabel(value) {
  return formatScientificTickLabel(value);
}

/**
 * Log-axis tick config for scatter/line charts.
 * Explicit decade ticks labeled as 1E-05, 1E-04, 1E+00, etc.
 */
export function logAxisPowerTickArrayConfig(values) {
  const positiveValues = (Array.isArray(values) ? values : [])
    .map((v) => (typeof v === 'number' ? v : parseFloat(v)))
    .filter((v) => Number.isFinite(v) && v > 0);
  if (!positiveValues.length) {
    return { ...plotlyLogAxisTickConfig() };
  }

  const minValue = Math.min(...positiveValues);
  const maxValue = Math.max(...positiveValues);
  const minExponent = Math.floor(Math.log10(minValue));
  const maxExponent = Math.ceil(Math.log10(maxValue));

  const tickvals = [];
  const ticktext = [];
  for (let exponent = minExponent; exponent <= maxExponent; exponent += 1) {
    const tickValue = Math.pow(10, exponent);
    tickvals.push(tickValue);
    ticktext.push(formatScientificTickLabel(tickValue));
  }

  return {
    tickmode: 'array',
    tickvals,
    ticktext,
    minor: { showticklabels: false },
    showexponent: 'none',
  };
}

function formatDecadeTickLabel(v) {
  return formatScientificTickLabel(v);
}

/**
 * Explicit powers-of-ten ticks for log axes (avoids Plotly dtick/minor quirks on scatter plots).
 */
export function logAxisExplicitDecades(minPos, maxPos) {
  if (!isFinite(minPos) || !isFinite(maxPos) || minPos <= 0 || maxPos <= 0) {
    return {
      ...plotlyLogAxisTickConfig(),
      autorange: true,
    };
  }
  const lo = Math.min(minPos, maxPos);
  const hi = Math.max(minPos, maxPos);
  const e0 = Math.floor(Math.log10(lo));
  const e1 = Math.ceil(Math.log10(hi));
  const tickvals = [];
  for (let e = e0; e <= e1; e++) {
    tickvals.push(Math.pow(10, e));
  }
  return {
    tickmode: 'array',
    tickvals,
    ticktext: tickvals.map(formatDecadeTickLabel),
    range: [e0, e1],
    autorange: false,
    minor: { showticklabels: false, showgrid: false, ticks: '' },
    showexponent: 'none',
  };
}

/** Collect positive y values from multi-series chart data for log tick ranges. */
export function collectPositiveYValuesFromSeries(chartSeries) {
  const values = [];
  for (const series of chartSeries || []) {
    for (const y of series.y || []) {
      const n = typeof y === 'number' ? y : parseFloat(y);
      if (Number.isFinite(n) && n > 0) values.push(n);
    }
  }
  return values;
}

/** Y-axis config for mole/mass fraction charts with optional log tick labels. */
export function buildFractionYAxisConfig(scale, chartSeries, options = {}) {
  const axis = {
    title: options.title,
    type: scale === 'log' ? 'log' : 'linear',
    automargin: true,
    gridcolor: options.gridcolor,
  };

  if (scale === 'log') {
    if (options.simpleLog) {
      const yValues = options.yValues ?? collectPositiveYValuesFromSeries(chartSeries);
      Object.assign(
        axis,
        yValues.length
          ? logAxisSimpleDecadeTickConfig(yValues)
          : plotlyLogAxisTickConfig()
      );
    } else {
      Object.assign(axis, logAxisPowerTickArrayConfig(collectPositiveYValuesFromSeries(chartSeries)));
    }
  }

  return axis;
}

/** Simple log ticks (0.01, 0.1, 1, 10…) for scatter charts with moderate value ranges. */
export function buildSimpleScatterLogYAxisConfig(scale, options = {}) {
  const axis = {
    type: scale === "log" ? "log" : "linear",
    automargin: options.automargin ?? true,
  };

  if (options.title != null) {
    axis.title = options.title;
  }
  if (options.gridcolor != null) {
    axis.gridcolor = options.gridcolor;
  }
  if (scale === "log") {
    const yValues = options.yValues;
    Object.assign(
      axis,
      Array.isArray(yValues) && yValues.length
        ? logAxisSimpleDecadeTickConfig(yValues)
        : plotlyLogAxisTickConfig()
    );
  }

  return axis;
}

/** Rebuild y-axis when toggling log/linear in expanded plot modals. */
export function applyScatterModalYAxisScale(layout, axisType, yValues) {
  const modalLayout = {
    ...layout,
    yaxis: {
      ...layout.yaxis,
      type: axisType,
    },
  };

  if (axisType === "log") {
    Object.assign(
      modalLayout.yaxis,
      Array.isArray(yValues) && yValues.length
        ? logAxisSimpleDecadeTickConfig(yValues)
        : plotlyLogAxisTickConfig()
    );
  } else {
    for (const key of AXIS_TICK_STATE_KEYS) {
      delete modalLayout.yaxis[key];
    }
    modalLayout.yaxis.autorange = true;
  }

  return modalLayout;
}

/** Bottom margin for category x-axes with rotated component names. */
export function estimateRotatedCategoryBottomMargin(categoryLabels, options = {}) {
  const tickFontSize = options.tickFontSize ?? 10;
  const tickAngle = options.tickAngle ?? -45;
  const titleBlock = options.titleBlock ?? 36;

  const labels = (Array.isArray(categoryLabels) ? categoryLabels : []).map((label) => String(label));
  const longestChars = labels.reduce((max, label) => Math.max(max, label.length), 0);
  if (!longestChars) {
    return titleBlock + 48;
  }

  const charWidth = tickFontSize * 0.58;
  const labelHeight = tickFontSize * 1.2;
  const angleRad = (Math.abs(tickAngle) * Math.PI) / 180;
  const longestWidth = longestChars * charWidth;
  const rotatedDepth =
    longestWidth * Math.sin(angleRad) + labelHeight * Math.cos(angleRad);

  const minMargin = 120;
  return Math.min(200, Math.max(minMargin, Math.ceil(rotatedDepth + titleBlock + 12)));
}

/** Fixed card height — label room comes from Plotly margin.b, not extra card height. */
export function getCompositionChartColumnHeight(categoryLabels, options = {}) {
  const defaultHeight = options.defaultHeight ?? 400;
  const charThreshold = options.charThreshold ?? 12;

  const labels = (Array.isArray(categoryLabels) ? categoryLabels : []).map((label) => String(label));
  const longestChars = labels.reduce((max, label) => Math.max(max, label.length), 0);

  return longestChars >= charThreshold ? 415 : defaultHeight;
}
